/**
 * The CRM's review of an office's breakdown format (OfficeBreakdownFormatPanel):
 * what each blank shows, in words; the facts a reviewer can choose from; sample
 * values to see the sheet filled in; and the placement preview's drawing.
 *
 * Pure apart from `drawPlacement` (a canvas). The catalog is the same file the
 * exports and the backend use, so a choice offered here is one both understand.
 */
import { CATALOG, factType, parseFactKey } from "../TreatmentPlans/component/Breakdown/print/customFormat/formatFacts";

const FACTS = {};
CATALOG.facts.forEach((f) => { FACTS[f.key] = f; });
const LINES = {};
CATALOG.lines.forEach((l) => { LINES[l.key] = l; });
const ATTRS = {};
CATALOG.line_attrs.forEach((a) => { ATTRS[a.attr] = a; });

// Which formats a fact of each type may be written in — the backend's
// validator.FORMATS_FOR_TYPE; anything else is refused there.
export const FORMATS_FOR_TYPE = {
  text: ["text"],
  date: ["date", "text"],
  money: ["money", "number", "text"],
  number: ["number", "text"],
  percent: ["percent", "number", "text"],
  months: ["months", "number", "text"],
  yes_no: ["yes_no", "text"],
  enum: ["text"],
};

/** A fact key in words: "Recall exam — months between", "01204 — coverage %". */
export function factLabel(key) {
  if (!key) return "Nothing (left empty)";
  const parsed = parseFactKey(key);
  if (!parsed) return key;
  if (parsed.kind === "fact") return FACTS[key].label;
  const attr = ATTRS[parsed.attr].label;
  return parsed.kind === "line" ? `${LINES[parsed.line].label} — ${attr}` : `${parsed.code} — ${attr}`;
}

/** Whether we collect this fact today ("Active family members" isn't). */
export function factCollected(key) {
  const fact = key && FACTS[key];
  return !fact || fact.available !== false;
}

/** The 5-digit codes this sheet prints or binds. */
export function sheetCodes(template) {
  const codes = {};
  ((template && template.fields) || []).forEach((f) => {
    const parsed = f.fact && parseFactKey(f.fact);
    if (parsed && parsed.kind === "code") codes[parsed.code] = true;
    (String(f.printed_label || "").match(/\d{5}/g) || []).forEach((c) => { codes[c] = true; });
  });
  return Object.keys(codes).sort();
}

/** The fact picker's options, grouped: plan facts, this sheet's codes, procedure lines. */
export function factOptions(template) {
  const facts = CATALOG.facts.map((f) => ({
    value: f.key, label: f.available === false ? `${f.label} (not collected yet)` : f.label,
  }));
  const codes = [];
  sheetCodes(template).forEach((code) => CATALOG.line_attrs.forEach((a) => {
    codes.push({ value: `code.${code}.${a.attr}`, label: `${code} — ${a.label}` });
  }));
  const lines = [];
  CATALOG.lines.forEach((l) => CATALOG.line_attrs.forEach((a) => {
    lines.push({ value: `line.${l.key}.${a.attr}`, label: `${l.label} — ${a.label}` });
  }));
  return [
    { label: "Patient, plan & maximums", options: facts },
    { label: "Codes on this sheet", options: codes },
    { label: "Procedure lines", options: lines },
  ].filter((g) => g.options.length);
}

/** The formats a blank showing `key` may use. */
export function formatsFor(key) {
  return FORMATS_FOR_TYPE[factType(key)] || ["text"];
}

/** The values a choice (a check-mark blank) can stand for, or null when it's a number. */
export function choiceValues(key) {
  if (!key) return null;
  const parsed = parseFactKey(key);
  if (parsed && parsed.kind === "fact") return FACTS[key].values || null;
  if (parsed) return ATTRS[parsed.attr].values || null;
  return null;
}

/** What a blank shows, in a few words — the placement preview's label. */
export function blankCaption(field) {
  if (!field.fact) return "(left empty)";
  if (field.render === "mark") return `✓ if ${field.mark_value}`;
  return factLabel(field.fact);
}

/** How the preview colours a blank: unbound, not collected, unsure, or fine. */
export function blankState(field) {
  if (!field.fact) return "unbound";
  if (!factCollected(field.fact)) return "not_collected";
  if (field.confidence === "low") return "unsure";
  return "bound";
}

export const STATE_COLOURS = {
  bound: "#2e8b3a", unsure: "#e08600", not_collected: "#c62828", unbound: "#8a8f98",
};

const SAMPLE_TEXT = {
  "patient.name": "Jane Sample", "member.name": "John Sample", "carrier.name": "Sun Life",
  "carrier.phone": "(800) 555-0123", "plan.group_number": "123456", "plan.member_id": "987654321",
  "plan.plan_name": "Standard Dental", "plan.fee_guide": "2026 ODA", "plan.ortho_ages": "to age 18",
  "plan.benefit_year": "Jan 1, 2026 – Dec 31, 2026", "meta.source": "ProviderConnect",
};

/** Made-up values, one per blank, to see the sheet filled in before going live. */
export function sampleValues(template, today = new Date()) {
  const iso = today.toISOString().slice(0, 10);
  const values = {};
  ((template && template.fields) || []).forEach((f) => {
    if (!f.fact) {
      values[f.id] = null;
      return;
    }
    const parsed = parseFactKey(f.fact);
    const attr = parsed && parsed.kind !== "fact" ? parsed.attr : null;
    const type = factType(f.fact);
    if (SAMPLE_TEXT[f.fact]) values[f.id] = SAMPLE_TEXT[f.fact];
    else if (attr === "summary") values[f.id] = "80%, every 9 mo";
    else if (attr === "frequency") values[f.id] = "1 every 9 mo";
    else if (f.fact === "plan.fee_guide_year") values[f.id] = today.getFullYear();
    else if (type === "date") values[f.id] = iso;
    else if (type === "money") values[f.id] = 1500;
    else if (type === "percent") values[f.id] = 80;
    else if (type === "months") values[f.id] = 9;
    else if (type === "number") values[f.id] = 18;
    else if (type === "yes_no") values[f.id] = "yes";
    else if (type === "enum") values[f.id] = (choiceValues(f.fact) || [null])[0];
    else values[f.id] = "Sample";
  });
  return values;
}

/**
 * Draw the placement preview for one page on `ctx`: the sheet, then every blank
 * outlined in its state's colour with what it will show. `hover` (a blank id)
 * is drawn on top, highlighted.
 */
export function drawPlacement(ctx, template, pageIndex, image, { hover = null } = {}) {
  const page = template.pages[pageIndex];
  ctx.drawImage(image, 0, 0, page.width, page.height);
  const fields = template.fields.filter((f) => f.page === pageIndex);
  const ordered = fields.filter((f) => f.id !== hover).concat(fields.filter((f) => f.id === hover));
  ordered.forEach((f) => {
    const colour = STATE_COLOURS[blankState(f)];
    const lit = f.id === hover;
    // Shown at about two thirds of its size in the drawer: a little larger than the sheet's print.
    const size = Math.max(13, Math.min(24, (Number(f.text_px) || 14) * 1.05));
    ctx.save();
    if (lit) {
      ctx.fillStyle = "rgba(255, 214, 0, 0.35)";
      const top = Math.min(f.y1, f.y2) - 2.2 * size;
      ctx.fillRect(f.x1 - 4, top, f.x2 - f.x1 + 8, Math.max(f.y1, f.y2) - top + 6);
    }
    ctx.strokeStyle = colour;
    ctx.lineWidth = lit ? 4 : 2;
    if (f.kind === "box") {
      ctx.strokeRect(f.x1, f.y1, f.x2 - f.x1, f.y2 - f.y1);
    } else {
      ctx.beginPath();
      ctx.moveTo(f.x1, f.y1);
      ctx.lineTo(f.x2, f.y2);
      ctx.stroke();
    }
    ctx.font = `${lit ? "bold " : ""}${size}px Helvetica, Arial, sans-serif`;
    ctx.fillStyle = colour;
    ctx.fillText(blankCaption(f).slice(0, 60), f.x1 + 2, Math.min(f.y1, f.y2) - 4);
    ctx.restore();
  });
}
